import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Grid, Paper, Typography, Button, Chip, Alert, CircularProgress,
  TextField, MenuItem, Select, FormControl, InputLabel, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  TableSortLabel, ToggleButton, ToggleButtonGroup, Tabs, Tab,
  Collapse, LinearProgress, Skeleton,
} from "@mui/material";
import {
  TrendingUp, TrendingDown, ShowChart, BarChart as BarChartIcon,
  FilterList, Download, Refresh, AttachMoney, Insights,
  GridView, TableChart, CalendarMonth, Schedule,
  PieChart as PieChartIcon,
} from "@mui/icons-material";
import { Chart as ChartJS, registerables } from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import AnalyticsService from "../services/AnalyticsService";

ChartJS.register(...registerables);

// ============================================================================
// UTILITY HELPERS
// ============================================================================
const fmt = (n, decimals = 2) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toFixed(decimals);
};
const fmtUsd = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const abs = Math.abs(n);
  const prefix = n < 0 ? "-$" : "$";
  return `${prefix}${abs.toFixed(2)}`;
};
const fmtPct = (n) => (n === null || n === undefined || isNaN(n) ? "—" : `${Number(n).toFixed(1)}%`);
const pnlColor = (n) => (n > 0 ? "#00FF94" : n < 0 ? "#EF4444" : "#9CA3AF");

const TIME_PRESETS = [
  { label: "1H", value: "1h" },
  { label: "24H", value: "24h" },
  { label: "7D", value: "7d" },
  { label: "30D", value: "30d" },
  { label: "YTD", value: "ytd" },
  { label: "All", value: "all" },
];

function getTimeRange(preset) {
  const now = new Date();
  let from = null;
  switch (preset) {
    case "1h": from = new Date(now.getTime() - 3600000); break;
    case "24h": from = new Date(now.getTime() - 86400000); break;
    case "7d": from = new Date(now.getTime() - 7 * 86400000); break;
    case "30d": from = new Date(now.getTime() - 30 * 86400000); break;
    case "ytd": from = new Date(now.getFullYear(), 0, 1); break;
    case "all": default: return {};
  }
  return { from: from?.toISOString(), to: now.toISOString() };
}

// ============================================================================
// STATUS KPI CARD
// ============================================================================
function KpiCard({ title, value, subtitle, icon, color, trend }) {
  return (
    <Paper sx={{
      p: 2.5, height: "100%", position: "relative", overflow: "hidden",
      background: 'rgba(22, 27, 34, 0.75)',
      backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 4px 30px rgba(0,0,0,0.4)',
      borderRadius: 2,
      transition: 'all 0.3s ease',
      '&:hover': { borderColor: 'rgba(255,255,255,0.15)', background: 'rgba(30,30,30,0.7)' },
    }}>
      <Box sx={{
        position: 'absolute', left: 0, top: '10%', bottom: '10%', width: 3,
        background: color || '#3b82f6', borderRadius: '0 4px 4px 0',
        boxShadow: `0 0 10px ${color || '#3b82f6'}99`,
      }} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: 'relative', zIndex: 1 }}>
        <Box>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.45)' }}>
            {title}
          </Typography>
          <Typography sx={{
            fontSize: '1.6rem', fontWeight: 700, color: 'white', mt: 0.5,
            textShadow: color && (color.includes('4caf50') || color.includes('00FF94')) ? '0 0 10px rgba(0,255,148,0.4)' : 'none',
          }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', mt: 0.25 }}>{subtitle}</Typography>
          )}
        </Box>
        <Box sx={{ color: color || "#3b82f6", opacity: 0.7, '& .MuiSvgIcon-root': { fontSize: 28 } }}>{icon}</Box>
      </Box>
      {trend !== undefined && (
        <Chip
          size="small"
          label={trend > 0 ? `+${fmt(trend)}%` : `${fmt(trend)}%`}
          sx={{ mt: 1, bgcolor: trend > 0 ? "rgba(0,255,148,0.12)" : "rgba(255,0,61,0.12)", color: trend > 0 ? '#00FF94' : '#EF4444', fontWeight: 600, fontSize: '0.65rem', border: `1px solid ${trend > 0 ? 'rgba(0,255,148,0.3)' : 'rgba(255,0,61,0.3)'}` }}
        />
      )}
      <Box sx={{ position: 'absolute', right: -16, bottom: -16, width: 96, height: 96, background: `${color || '#3b82f6'}15`, borderRadius: '50%', filter: 'blur(24px)', transition: 'all 0.5s' }} />
    </Paper>
  );
}

// ============================================================================
// P&L CALENDAR — Multi-view: Hourly | Daily | Monthly | Yearly
// ============================================================================
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTH_FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function pnlCellColor(pnl, count, maxAbs) {
  if (!count || count === 0) return "rgba(255,255,255,0.02)";
  const cap = maxAbs || 1;
  if (pnl > 0) {
    const i = Math.min(pnl / cap, 1);
    return `rgba(0,255,148,${0.08 + i * 0.45})`;
  }
  const i = Math.min(Math.abs(pnl) / cap, 1);
  return `rgba(255,0,61,${0.08 + i * 0.45})`;
}

// —— HOURLY VIEW (improved Day × Hour) ——
function HourlyView({ cells }) {
  if (!cells || cells.length === 0) return <Typography color="text.secondary" sx={{ p: 2 }}>No hourly data</Typography>;
  const dayMap = [1,2,3,4,5,6,0];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const vals = cells.filter(c => c.count > 0).map(c => Math.abs(c.pnl));
  const maxAbs = Math.max(...vals, 0.01);
  const getCell = (d, h) => cells.find(c => c.day === d && c.hour === h) || { pnl: 0, count: 0, winRate: 0 };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: `48px repeat(24, 1fr)`, gap: "2px", minWidth: 680 }}>
        <Box sx={{ p: 0.5 }} />
        {hours.map(h => (
          <Box key={h} sx={{ textAlign: "center", fontSize: 9, fontWeight: 700, color: "text.secondary", py: 0.3 }}>
            {h.toString().padStart(2,"0")}
          </Box>
        ))}
        {dayMap.map((utcDay, rowIdx) => (
          <React.Fragment key={utcDay}>
            <Box sx={{ display: "flex", alignItems: "center", fontSize: 11, fontWeight: 700, color: "text.secondary", pr: 0.5 }}>
              {DAY_LABELS[rowIdx]}
            </Box>
            {hours.map(h => {
              const c = getCell(utcDay, h);
              return (
                <Tooltip key={h} arrow placement="top" title={
                  <Box sx={{ fontSize: 11 }}>
                    <Box sx={{ fontWeight: 700 }}>{DAY_LABELS[rowIdx]} {h}:00 UTC</Box>
                    <Box>P&L: <span style={{ color: c.pnl >= 0 ? "#00FF94" : "#EF4444" }}>{fmtUsd(c.pnl)}</span></Box>
                    <Box>Trades: {c.count} | WR: {fmtPct(c.winRate)}</Box>
                  </Box>
                }>
                  <Box sx={{
                    bgcolor: pnlCellColor(c.pnl, c.count, maxAbs),
                    borderRadius: "3px",
                    height: 28,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 9, fontWeight: c.count > 0 ? 700 : 400,
                    color: c.count > 0 ? "#fff" : "rgba(255,255,255,0.15)",
                    cursor: "pointer",
                    transition: "transform 0.1s",
                    "&:hover": { transform: "scale(1.15)", zIndex: 2 },
                  }}>
                    {c.count > 0 ? c.count : ""}
                  </Box>
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

// —— DAILY VIEW (Month Calendar Grid) ——
function DailyView({ daily, initialDate, onDrillDown }) {
  const [viewDate, setViewDate] = useState(() => initialDate ? new Date(initialDate) : new Date());
  if (!daily || daily.length === 0) return <Typography color="text.secondary" sx={{ p: 2 }}>No daily data</Typography>;

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDow = (firstDay.getDay() + 6) % 7; // Mon=0

  const dailyMap = {};
  daily.forEach(d => { dailyMap[d.date] = d; });
  const vals = daily.map(d => Math.abs(d.pnl));
  const maxAbs = Math.max(...vals, 0.01);

  const weeks = [];
  let dayNum = 1 - startDow;
  while (dayNum <= daysInMonth) {
    const week = [];
    for (let dow = 0; dow < 7; dow++, dayNum++) {
      if (dayNum < 1 || dayNum > daysInMonth) {
        week.push(null);
      } else {
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
        week.push({ dayNum, dateStr, data: dailyMap[dateStr] || null });
      }
    }
    weeks.push(week);
  }

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));
  const today = new Date().toISOString().split("T")[0];

  // Month summary
  const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;
  const monthDays = daily.filter(d => d.date.startsWith(monthKey));
  const monthPnl = monthDays.reduce((s, d) => s + d.pnl, 0);
  const monthTrades = monthDays.reduce((s, d) => s + d.count, 0);
  const monthWins = monthDays.reduce((s, d) => s + d.wins, 0);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <IconButton size="small" onClick={prevMonth}><Typography sx={{ fontWeight: 700 }}>&lt;</Typography></IconButton>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{MONTH_FULL[month]} {year}</Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 0.5 }}>
            <Chip size="small" label={`P&L: ${fmtUsd(monthPnl)}`} sx={{ fontWeight: 700, bgcolor: monthPnl >= 0 ? "rgba(76,175,80,0.15)" : "rgba(244,67,54,0.15)", color: pnlColor(monthPnl) }} />
            <Chip size="small" label={`${monthTrades} trades`} variant="outlined" sx={{ fontSize: "0.7rem" }} />
            <Chip size="small" label={`WR: ${monthTrades > 0 ? ((monthWins / monthTrades) * 100).toFixed(0) : 0}%`} variant="outlined" sx={{ fontSize: "0.7rem" }} />
          </Box>
        </Box>
        <IconButton size="small" onClick={nextMonth}><Typography sx={{ fontWeight: 700 }}>&gt;</Typography></IconButton>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "3px" }}>
        {DAY_LABELS.map(d => (
          <Box key={d} sx={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "text.secondary", py: 0.5 }}>{d}</Box>
        ))}
        {weeks.flat().map((cell, i) => {
          if (!cell) return <Box key={i} sx={{ height: { xs: 52, sm: 64 }, borderRadius: 1 }} />;
          const d = cell.data;
          const isToday = cell.dateStr === today;
          return (
            <Tooltip key={i} arrow title={
              d ? (
                <Box sx={{ fontSize: 11 }}>
                  <Box sx={{ fontWeight: 700 }}>{cell.dateStr}</Box>
                  <Box>P&L: <span style={{ color: d.pnl >= 0 ? "#00FF94" : "#EF4444" }}>{fmtUsd(d.pnl)}</span></Box>
                  <Box>Trades: {d.count} | WR: {fmtPct(d.winRate)}</Box>
                  <Box>Gross: {fmtUsd(d.grossPnl)} | Fees: {fmtUsd(d.fees)}</Box>
                </Box>
              ) : `${cell.dateStr} — No trades`
            }>
              <Box onDoubleClick={() => d && onDrillDown?.(cell.dateStr)} sx={{
                height: { xs: 56, sm: 70 },
                borderRadius: 1,
                bgcolor: d ? pnlCellColor(d.pnl, d.count, maxAbs) : "rgba(255,255,255,0.02)",
                border: isToday ? "1px solid #3b82f6" : "1px solid rgba(255,255,255,0.05)",
                boxShadow: isToday ? '0 0 15px rgba(59,130,246,0.4)' : d ? (d.pnl > 0 ? '0 0 20px rgba(0,255,148,0.1) inset' : '0 0 20px rgba(255,0,61,0.1) inset') : 'none',
                backdropFilter: 'blur(8px)',
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                cursor: d ? 'zoom-in' : 'default',
                transition: "transform 0.2s ease, border-color 0.2s ease",
                "&:hover": { transform: d ? "scale(1.05)" : 'none', borderColor: 'rgba(255,255,255,0.2)', zIndex: 10 },
              }}>
                <Typography sx={{ fontSize: 11, fontWeight: isToday ? 800 : 500, color: isToday ? "#3b82f6" : d ? '#fff' : 'rgba(255,255,255,0.35)' }}>
                  {cell.dayNum}
                </Typography>
                {d && (
                  <>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: d.pnl >= 0 ? "#00FF94" : "#EF4444", lineHeight: 1, textShadow: d.pnl >= 0 ? '0 0 10px rgba(0,255,148,0.6)' : '0 0 10px rgba(255,0,61,0.6)' }}>
                      {d.pnl >= 0 ? "+" : ""}${d.pnl.toFixed(2)}
                    </Typography>
                    <Typography sx={{ fontSize: 8, color: d.pnl >= 0 ? 'rgba(0,255,148,0.5)' : 'rgba(255,0,61,0.5)', lineHeight: 1, mt: 0.2 }}>
                      {d.count}t
                    </Typography>
                  </>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

// —— MONTHLY VIEW (Year × Month grid) ——
function MonthlyView({ monthly, onDrillDown }) {
  if (!monthly || monthly.length === 0) return <Typography color="text.secondary" sx={{ p: 2 }}>No monthly data</Typography>;
  const vals = monthly.map(m => Math.abs(m.pnl));
  const maxAbs = Math.max(...vals, 0.01);

  // Group by year
  const years = {};
  monthly.forEach(m => {
    const y = m.month.split("-")[0];
    if (!years[y]) years[y] = {};
    const mo = parseInt(m.month.split("-")[1]) - 1;
    years[y][mo] = m;
  });

  return (
    <Box>
      {Object.entries(years).sort((a, b) => b[0].localeCompare(a[0])).map(([year, months]) => {
        const yearPnl = Object.values(months).reduce((s, m) => s + m.pnl, 0);
        const yearTrades = Object.values(months).reduce((s, m) => s + m.count, 0);
        return (
          <Box key={year} sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{year}</Typography>
              <Chip size="small" label={`P&L: ${fmtUsd(yearPnl)}`} sx={{ fontWeight: 700, bgcolor: yearPnl >= 0 ? "rgba(76,175,80,0.15)" : "rgba(244,67,54,0.15)", color: pnlColor(yearPnl) }} />
              <Chip size="small" label={`${yearTrades} trades`} variant="outlined" sx={{ fontSize: "0.7rem" }} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: "6px" }}>
              {Array.from({ length: 12 }, (_, mi) => {
                const m = months[mi];
                return (
                  <Tooltip key={mi} arrow title={
                    m ? (
                      <Box sx={{ fontSize: 11 }}>
                        <Box sx={{ fontWeight: 700 }}>{MONTH_FULL[mi]} {year}</Box>
                        <Box>P&L: <span style={{ color: m.pnl >= 0 ? "#00FF94" : "#EF4444" }}>{fmtUsd(m.pnl)}</span></Box>
                        <Box>Trades: {m.count} | WR: {fmtPct(m.winRate)}</Box>
                        <Box>Trading Days: {m.tradingDays}</Box>
                      </Box>
                    ) : `${MONTH_FULL[mi]} ${year} — No trades`
                  }>
                    <Box onDoubleClick={() => m && onDrillDown?.(parseInt(year), mi)} sx={{
                      height: 72,
                      borderRadius: 1.5,
                      bgcolor: m ? pnlCellColor(m.pnl, m.count, maxAbs) : "rgba(255,255,255,0.02)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      cursor: m ? 'zoom-in' : 'default',
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": { transform: m ? "scale(1.06)" : 'none', boxShadow: m ? "0 4px 16px rgba(0,0,0,0.3)" : 'none', zIndex: 2 },
                    }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 0.3 }}>
                        {MONTH_NAMES[mi]}
                      </Typography>
                      {m ? (
                        <>
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: m.pnl >= 0 ? "#00FF94" : "#EF4444", lineHeight: 1 }}>
                            {m.pnl >= 0 ? "+" : ""}{m.pnl.toFixed(2)}
                          </Typography>
                          <Typography sx={{ fontSize: 9, color: "rgba(255,255,255,0.5)", mt: 0.3 }}>
                            {m.count}t / {m.tradingDays}d
                          </Typography>
                        </>
                      ) : (
                        <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.2)" }}>—</Typography>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// —— YEARLY VIEW (Summary cards) ——
function YearlyView({ yearly, onDrillDown }) {
  if (!yearly || yearly.length === 0) return <Typography color="text.secondary" sx={{ p: 2 }}>No yearly data</Typography>;
  const vals = yearly.map(y => Math.abs(y.pnl));
  const maxAbs = Math.max(...vals, 0.01);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 2 }}>
      {yearly.sort((a, b) => b.year.localeCompare(a.year)).map(y => (
        <Tooltip key={y.year} title="Double-click to explore months" placement="top" arrow>
        <Box onDoubleClick={() => onDrillDown?.(parseInt(y.year))} sx={{
          p: 2,
          borderRadius: 1.5,
          bgcolor: pnlCellColor(y.pnl, y.count, maxAbs),
          border: "1px solid rgba(255,255,255,0.08)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          cursor: 'zoom-in',
          "&:hover": { transform: "scale(1.03)", boxShadow: '0 0 20px rgba(255,255,255,0.1)' },
        }}>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>{y.year}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: y.pnl >= 0 ? "#00FF94" : "#EF4444", mb: 1 }}>
            {y.pnl >= 0 ? "+" : ""}{fmtUsd(y.pnl)}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            <Chip size="small" label={`${y.count} trades`} variant="outlined" sx={{ fontSize: "0.7rem" }} />
            <Chip size="small" label={`WR: ${fmtPct(y.winRate)}`} variant="outlined" sx={{ fontSize: "0.7rem" }} />
            <Chip size="small" label={`${y.activeMonths} months`} variant="outlined" sx={{ fontSize: "0.7rem" }} />
            <Chip size="small" label={`${y.tradingDays} days`} variant="outlined" sx={{ fontSize: "0.7rem" }} />
          </Box>
        </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

// —— COMBINED CALENDAR COMPONENT with Drill-Down ——
const VIEW_LEVELS = ['yearly', 'monthly', 'daily', 'hourly'];

function PnlCalendar({ data }) {
  const [view, setView] = useState("daily");
  const [drillStack, setDrillStack] = useState([]); // [{view, label, year?, month?}]
  const [drillYear, setDrillYear] = useState(null);
  const [drillMonth, setDrillMonth] = useState(null); // 0-indexed
  const [drillDay, setDrillDay] = useState(null);
  const [transitioning, setTransitioning] = useState(false);

  const animateTransition = (cb) => {
    setTransitioning(true);
    setTimeout(() => { cb(); setTimeout(() => setTransitioning(false), 50); }, 150);
  };

  // Drill-down handlers
  const drillFromYearly = (year) => {
    animateTransition(() => {
      setDrillStack(prev => [...prev, { view: 'yearly', label: 'All Years' }]);
      setDrillYear(year);
      setView('monthly');
    });
  };

  const drillFromMonthly = (year, monthIdx) => {
    animateTransition(() => {
      setDrillStack(prev => [...prev, { view: 'monthly', label: `${year}`, year: drillYear }]);
      setDrillYear(year);
      setDrillMonth(monthIdx);
      setView('daily');
    });
  };

  const drillFromDaily = (dateStr) => {
    animateTransition(() => {
      const parts = dateStr.split('-');
      setDrillStack(prev => [...prev, { view: 'daily', label: `${MONTH_FULL[drillMonth ?? new Date().getMonth()]} ${drillYear ?? new Date().getFullYear()}`, year: drillYear, month: drillMonth }]);
      setDrillDay(dateStr);
      setView('hourly');
    });
  };

  const goBack = useCallback(() => {
    if (drillStack.length === 0) return;
    animateTransition(() => {
      const prev = drillStack[drillStack.length - 1];
      setDrillStack(s => s.slice(0, -1));
      setView(prev.view);
      if (prev.view === 'yearly') { setDrillYear(null); setDrillMonth(null); setDrillDay(null); }
      else if (prev.view === 'monthly') { setDrillYear(prev.year); setDrillMonth(null); setDrillDay(null); }
      else if (prev.view === 'daily') { setDrillYear(prev.year); setDrillMonth(prev.month); setDrillDay(null); }
    });
  }, [drillStack]);

  const goToLevel = (idx) => {
    if (idx >= drillStack.length) return;
    animateTransition(() => {
      const target = drillStack[idx];
      setDrillStack(s => s.slice(0, idx));
      setView(target.view);
      if (target.view === 'yearly') { setDrillYear(null); setDrillMonth(null); setDrillDay(null); }
      else if (target.view === 'monthly') { setDrillYear(target.year); setDrillMonth(null); setDrillDay(null); }
      else if (target.view === 'daily') { setDrillYear(target.year); setDrillMonth(target.month); setDrillDay(null); }
    });
  };

  const handleManualViewChange = (_, v) => {
    if (!v) return;
    animateTransition(() => {
      setView(v);
      setDrillStack([]);
      setDrillYear(null);
      setDrillMonth(null);
      setDrillDay(null);
    });
  };

  // ESC key to go back
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && drillStack.length > 0) goBack(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goBack]);

  // Compute initialDate for DailyView when drilled from monthly
  const dailyInitialDate = drillYear != null && drillMonth != null
    ? `${drillYear}-${String(drillMonth + 1).padStart(2, '0')}-01`
    : null;

  // Current breadcrumb label
  const currentLabel = view === 'yearly' ? 'All Years'
    : view === 'monthly' ? (drillYear ? `${drillYear}` : 'Monthly')
    : view === 'daily' ? (drillYear && drillMonth != null ? `${MONTH_FULL[drillMonth]} ${drillYear}` : 'Daily')
    : view === 'hourly' ? (drillDay || 'Hourly') : '';

  if (!data) return <Typography color="text.secondary" sx={{ p: 2 }}>No heatmap data available</Typography>;

  return (
    <Box>
      {/* Header: Title + View Toggle */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarMonth sx={{ fontSize: 22, color: "primary.main" }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>P&L Calendar</Typography>
        </Box>
        <ToggleButtonGroup value={view} exclusive onChange={handleManualViewChange} size="small">
          <ToggleButton value="hourly" sx={{ textTransform: "none", fontSize: 12, px: 1.5 }}>
            <Schedule sx={{ fontSize: 14, mr: 0.5 }} /> Hourly
          </ToggleButton>
          <ToggleButton value="daily" sx={{ textTransform: "none", fontSize: 12, px: 1.5 }}>
            <GridView sx={{ fontSize: 14, mr: 0.5 }} /> Daily
          </ToggleButton>
          <ToggleButton value="monthly" sx={{ textTransform: "none", fontSize: 12, px: 1.5 }}>
            <CalendarMonth sx={{ fontSize: 14, mr: 0.5 }} /> Monthly
          </ToggleButton>
          <ToggleButton value="yearly" sx={{ textTransform: "none", fontSize: 12, px: 1.5 }}>
            <BarChartIcon sx={{ fontSize: 14, mr: 0.5 }} /> Yearly
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Breadcrumb Navigation */}
      {drillStack.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
          {drillStack.map((crumb, idx) => (
            <React.Fragment key={idx}>
              <Typography
                onClick={() => goToLevel(idx)}
                sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)', cursor: 'pointer', '&:hover': { color: '#00FF94' }, transition: 'color 0.2s' }}
              >
                {crumb.label}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.2)', mx: 0.3 }}>/</Typography>
            </React.Fragment>
          ))}
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#00FF94' }}>
            {currentLabel}
          </Typography>
          <Typography
            onClick={goBack}
            sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'rgba(255,255,255,0.35)', cursor: 'pointer', ml: 1.5, '&:hover': { color: '#fff' }, display: 'flex', alignItems: 'center', gap: 0.3 }}
          >
            ← Back
          </Typography>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
        {view === "hourly" && "Day of Week × Hour of Day (UTC). Green = profitable, Red = losing. Numbers show trade count."}
        {view === "daily" && "Calendar view showing daily P&L. Double-click a day to see hourly breakdown."}
        {view === "monthly" && "Monthly P&L overview. Double-click a month to see daily breakdown."}
        {view === "yearly" && "Annual performance summary. Double-click a year to see monthly breakdown."}
      </Typography>

      {/* Animated Content */}
      <Box sx={{ transition: 'opacity 0.15s ease, transform 0.2s ease', opacity: transitioning ? 0 : 1, transform: transitioning ? 'scale(0.98)' : 'scale(1)' }}>
        {view === "hourly" && <HourlyView cells={data.cells} />}
        {view === "daily" && <DailyView daily={data.daily} initialDate={dailyInitialDate} onDrillDown={drillFromDaily} />}
        {view === "monthly" && <MonthlyView monthly={data.monthly} onDrillDown={drillFromMonthly} />}
        {view === "yearly" && <YearlyView yearly={data.yearly} onDrillDown={drillFromYearly} />}
      </Box>

      {/* Legend */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, pt: 1.5, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, bgcolor: "rgba(0,255,148,0.6)", borderRadius: '50%', boxShadow: '0 0 8px rgba(0,255,148,0.8)' }} />
          <Typography variant="caption" color="text.secondary">Profitable</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, bgcolor: "rgba(255,0,61,0.6)", borderRadius: '50%', boxShadow: '0 0 8px rgba(255,0,61,0.8)' }} />
          <Typography variant="caption" color="text.secondary">Losing</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, bgcolor: "rgba(255,255,255,0.03)", borderRadius: '50%', border: "1px solid rgba(255,255,255,0.15)" }} />
          <Typography variant="caption" color="text.secondary">No trades</Typography>
        </Box>
        <Typography variant="caption" sx={{ ml: "auto", fontSize: '0.6rem', color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          Color intensity = P&L magnitude
        </Typography>
      </Box>
    </Box>
  );
}

// ============================================================================
// BREAKDOWN TABLE COMPONENT
// ============================================================================
function BreakdownTable({ data, title }) {
  if (!data || data.length === 0) {
    return <Typography color="text.secondary" sx={{ p: 1 }}>No data</Typography>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>{title}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Trades</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Win Rate</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Net P&L</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Profit Factor</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Avg Win</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Avg Loss</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Fees</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, i) => (
            <TableRow key={i} sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.03)" } }}>
              <TableCell sx={{ fontWeight: 600 }}>{row.name || row.period}</TableCell>
              <TableCell align="right">{row.totalTrades}</TableCell>
              <TableCell align="right" sx={{ color: row.winRate >= 50 ? "#00FF94" : "#EF4444" }}>
                {fmtPct(row.winRate)}
              </TableCell>
              <TableCell align="right" sx={{ color: pnlColor(row.netPnl), fontWeight: 600 }}>
                {fmtUsd(row.netPnl)}
              </TableCell>
              <TableCell align="right" sx={{ color: row.profitFactor >= 1 ? "#00FF94" : "#EF4444" }}>
                {row.profitFactor === Infinity ? "∞" : fmt(row.profitFactor)}
              </TableCell>
              <TableCell align="right" sx={{ color: "#00FF94" }}>{fmtUsd(row.avgWin)}</TableCell>
              <TableCell align="right" sx={{ color: "#EF4444" }}>{fmtUsd(row.avgLoss)}</TableCell>
              <TableCell align="right">{fmtUsd(row.feesTotal)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ============================================================================
// TRADES TABLE COMPONENT
// ============================================================================
function TradesTable({ filters }) {
  const [trades, setTrades] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("entryTime");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(false);

  const loadTrades = useCallback(async () => {
    setLoading(true);
    try {
      const result = await AnalyticsService.getTrades({
        ...filters,
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortDir,
      });
      setTrades(result.trades || []);
      setTotal(result.total || 0);
    } catch (e) {
      console.error("Error loading trades:", e);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage, sortBy, sortDir]);

  useEffect(() => { loadTrades(); }, [loadTrades]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
  };

  const exportCSV = () => {
    const headers = ["Symbol", "Side", "Broker", "Mode", "Leverage", "Entry Price", "Exit Price", "Net P&L", "Net P&L%", "Fees", "Duration", "Exit Reason", "Status", "Entry Time", "Exit Time"];
    const rows = trades.map(t => [
      t.symbol, t.side, t.broker, t.tradeMode, t.leverage,
      t.entryPrice, t.exitPrice || "", t.pnlNet, t.pnlNetPct, t.feesTotal,
      Math.round(t.durationSeconds / 60) + "m", t.exitReason, t.status, t.entryTime, t.exitTime || "",
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trades_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(trades, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trades_export_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { id: "symbol", label: "Symbol" },
    { id: "strategyName", label: "Strategy" },
    { id: "side", label: "Side" },
    { id: "tradeMode", label: "Mode" },
    { id: "leverage", label: "Lev" },
    { id: "entryPrice", label: "Entry" },
    { id: "exitPrice", label: "Exit" },
    { id: "pnlNet", label: "Net P&L" },
    { id: "pnlNetPct", label: "P&L%" },
    { id: "feesTotal", label: "Fees" },
    { id: "durationSeconds", label: "Duration" },
    { id: "exitReason", label: "Exit Reason" },
    { id: "entryTime", label: "Entry Time" },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1, gap: 1 }}>
        <Button size="small" variant="outlined" startIcon={<Download />} onClick={exportCSV}>CSV</Button>
        <Button size="small" variant="outlined" startIcon={<Download />} onClick={exportJSON}>JSON</Button>
      </Box>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <TableContainer sx={{ maxHeight: 500 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col.id} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sortBy === col.id}
                    direction={sortBy === col.id ? sortDir : "asc"}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {trades.map((t, i) => (
              <TableRow key={t.tradeId || i} sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.03)" } }}>
                <TableCell sx={{ fontWeight: 600 }}>{t.symbol}</TableCell>
                <TableCell>
                  <Tooltip title={t.strategySnapshot ? (
                    <Box sx={{ p: 0.5 }}>
                      {Object.entries(t.strategySnapshot).filter(([k]) => k !== 'name').map(([k, v]) => (
                        <Typography key={k} variant="caption" display="block">{k}: <b>{String(v)}</b></Typography>
                      ))}
                    </Box>
                  ) : ""}>
                    <Typography variant="body2" sx={{ fontSize: 11, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: t.strategySnapshot ? "help" : "default" }}>
                      {t.strategyName || t.strategyId?.slice(0, 8) || "—"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={t.side} color={t.side === "buy" || t.side === "long" ? "success" : "error"} sx={{ fontSize: 10 }} />
                </TableCell>
                <TableCell sx={{ textTransform: "capitalize" }}>{t.tradeMode}</TableCell>
                <TableCell>{t.leverage}x</TableCell>
                <TableCell>{fmt(t.entryPrice, 4)}</TableCell>
                <TableCell>{t.exitPrice ? fmt(t.exitPrice, 4) : "—"}</TableCell>
                <TableCell sx={{ color: pnlColor(t.pnlNet), fontWeight: 600 }}>{fmtUsd(t.pnlNet)}</TableCell>
                <TableCell sx={{ color: pnlColor(t.pnlNetPct) }}>{fmtPct(t.pnlNetPct)}</TableCell>
                <TableCell>{fmtUsd(t.feesTotal)}</TableCell>
                <TableCell>{t.durationSeconds > 3600 ? `${(t.durationSeconds / 3600).toFixed(1)}h` : `${Math.round(t.durationSeconds / 60)}m`}</TableCell>
                <TableCell sx={{ fontSize: 11, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <Tooltip title={t.exitReason}><span>{t.exitReason || "—"}</span></Tooltip>
                </TableCell>
                <TableCell sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                  {new Date(t.entryTime).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </TableCell>
              </TableRow>
            ))}
            {trades.length === 0 && !loading && (
              <TableRow><TableCell colSpan={14} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No trades found</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
}

// ============================================================================
// CHART OPTIONS
// ============================================================================
const chartDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { labels: { color: "#ccc", font: { size: 11 } } },
    tooltip: { backgroundColor: "rgba(0,0,0,0.85)", titleFont: { size: 12 }, bodyFont: { size: 11 } },
  },
  scales: {
    x: { ticks: { color: "#999", font: { size: 10 } }, grid: { color: "rgba(255,255,255,0.05)" } },
    y: { ticks: { color: "#999", font: { size: 10 } }, grid: { color: "rgba(255,255,255,0.08)" } },
  },
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================
export default function TradingDashboard() {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePreset, setTimePreset] = useState("30d");
  const [granularity, setGranularity] = useState("day");
  const [activeTab, setActiveTab] = useState(0);

  // Data
  const [overview, setOverview] = useState(null);
  const [equityCurve, setEquityCurve] = useState(null);
  const [timeBuckets, setTimeBuckets] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [breakdowns, setBreakdowns] = useState(null);
  const [insights, setInsights] = useState(null);

  // Filters
  const [brokerFilter, setBrokerFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [symbolFilter, setSymbolFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("closed");
  const [showFilters, setShowFilters] = useState(false);

  const filters = useMemo(() => ({
    ...getTimeRange(timePreset),
    broker: brokerFilter || undefined,
    tradeMode: modeFilter || undefined,
    symbol: symbolFilter || undefined,
    status: statusFilter || undefined,
    granularity,
  }), [timePreset, granularity, brokerFilter, modeFilter, symbolFilter, statusFilter]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, ec, tb, hm, bd, ins] = await Promise.allSettled([
        AnalyticsService.getOverview(filters),
        AnalyticsService.getEquityCurve(filters),
        AnalyticsService.getTimeBuckets(filters),
        AnalyticsService.getHeatmaps(filters),
        AnalyticsService.getBreakdowns(filters),
        AnalyticsService.getInsights(filters),
      ]);
      if (ov.status === "fulfilled") setOverview(ov.value);
      if (ec.status === "fulfilled") setEquityCurve(ec.value);
      if (tb.status === "fulfilled") setTimeBuckets(tb.value);
      if (hm.status === "fulfilled") setHeatmapData(hm.value);
      if (bd.status === "fulfilled") setBreakdowns(bd.value);
      if (ins.status === "fulfilled") setInsights(ins.value);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadAllData(); }, [loadAllData]);

  // =========================================================================
  // CHART DATA
  // =========================================================================
  const equityChartData = useMemo(() => {
    if (!equityCurve?.points?.length) return null;
    return {
      labels: equityCurve.points.map(p => new Date(p.time).toLocaleDateString(undefined, { month: "short", day: "numeric" })),
      datasets: [
        {
          label: "Cumulative Net P&L",
          data: equityCurve.points.map(p => p.cumPnl),
          borderColor: "#00FF94",
          backgroundColor: "rgba(76,175,80,0.1)",
          fill: true,
          tension: 0.3,
          pointRadius: equityCurve.points.length > 50 ? 0 : 2,
          borderWidth: 2,
        },
        {
          label: "Drawdown",
          data: equityCurve.points.map(p => -p.drawdown),
          borderColor: "#EF4444",
          backgroundColor: "rgba(244,67,54,0.08)",
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 1,
        },
      ],
    };
  }, [equityCurve]);

  const pnlBarData = useMemo(() => {
    if (!equityCurve?.bucketedCurve?.length) return null;
    const buckets = equityCurve.bucketedCurve;
    return {
      labels: buckets.map(b => b.period),
      datasets: [{
        label: "Net P&L",
        data: buckets.map(b => b.netPnl),
        backgroundColor: buckets.map(b => b.netPnl >= 0 ? "rgba(76,175,80,0.7)" : "rgba(244,67,54,0.7)"),
        borderRadius: 2,
      }],
    };
  }, [equityCurve]);

  const winRateChartData = useMemo(() => {
    if (!timeBuckets?.length) return null;
    return {
      labels: timeBuckets.map(b => b.period),
      datasets: [
        {
          label: "Win Rate %",
          data: timeBuckets.map(b => b.winRate),
          borderColor: "#3b82f6",
          backgroundColor: "rgba(33,150,243,0.1)",
          fill: true,
          tension: 0.3,
          yAxisID: "y",
        },
        {
          label: "Profit Factor",
          data: timeBuckets.map(b => Math.min(b.profitFactor, 10)),
          borderColor: "#F97316",
          borderDash: [5, 3],
          tension: 0.3,
          yAxisID: "y1",
        },
      ],
    };
  }, [timeBuckets]);

  const feesChartData = useMemo(() => {
    if (!equityCurve?.bucketedCurve?.length) return null;
    const buckets = equityCurve.bucketedCurve;
    return {
      labels: buckets.map(b => b.period),
      datasets: [{
        label: "Fees",
        data: buckets.map(b => b.fees),
        backgroundColor: "rgba(255,152,0,0.6)",
        borderRadius: 2,
      }],
    };
  }, [equityCurve]);

  const exitReasonChart = useMemo(() => {
    if (!breakdowns?.byExitReason?.length) return null;
    const colors = ["#00FF94", "#EF4444", "#3b82f6", "#F97316", "#A855F7", "#3b82f6", "#EF4444", "#00FF94", "#F97316", "#6B7280"];
    return {
      labels: breakdowns.byExitReason.map(b => b.name),
      datasets: [{
        data: breakdowns.byExitReason.map(b => b.totalTrades),
        backgroundColor: breakdowns.byExitReason.map((_, i) => colors[i % colors.length]),
        borderWidth: 0,
      }],
    };
  }, [breakdowns]);

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1600, mx: "auto" }}>
      {/* HEADER */}
      <Box sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1.5,
        p: 2, borderRadius: 2,
        background: 'rgba(22,27,34,0.75)',
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 30px rgba(0,0,0,0.4)',
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShowChart sx={{ fontSize: 28, color: '#00FF94' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>Trading Dashboard</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.3, ml: 4.5 }}>
            Comprehensive performance analytics across all brokers, assets & strategies
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Tooltip title="Toggle Filters">
            <IconButton onClick={() => setShowFilters(!showFilters)} sx={{ color: showFilters ? '#00FF94' : '#9CA3AF' }}>
              <FilterList />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={loadAllData} disabled={loading} sx={{ color: '#9CA3AF', '&:hover': { color: '#00FF94' } }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ERROR */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* TIME PRESETS + GRANULARITY */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <ToggleButtonGroup size="small" value={timePreset} exclusive onChange={(_, v) => v && setTimePreset(v)}>
          {TIME_PRESETS.map(p => (
            <ToggleButton key={p.value} value={p.value} sx={{ px: 2, fontWeight: 600 }}>{p.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Granularity</InputLabel>
          <Select value={granularity} label="Granularity" onChange={(e) => setGranularity(e.target.value)}>
            <MenuItem value="hour">Hour</MenuItem>
            <MenuItem value="day">Day</MenuItem>
            <MenuItem value="week">Week</MenuItem>
            <MenuItem value="month">Month</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* FILTERS PANEL */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Broker" value={brokerFilter} onChange={e => setBrokerFilter(e.target.value)} placeholder="e.g., kucoin" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select value={modeFilter} label="Mode" onChange={e => setModeFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="spot">Spot</MenuItem>
                  <MenuItem value="margin">Margin</MenuItem>
                  <MenuItem value="futures">Futures</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Symbol" value={symbolFilter} onChange={e => setSymbolFilter(e.target.value)} placeholder="e.g., BTC/USDT" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* KPI STATUS PANEL */}
      {loading && !overview ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Grid item xs={6} sm={4} md={2} key={i}><Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} /></Grid>
          ))}
        </Grid>
      ) : overview ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard title="Total Trades" value={overview.totalTrades} subtitle={`${overview.openPositions} open`} icon={<BarChartIcon sx={{ fontSize: 40 }} />} color="#3b82f6" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard title="Win Rate" value={fmtPct(overview.winRate)} subtitle={`${overview.wins}W / ${overview.losses}L`} icon={<TrendingUp sx={{ fontSize: 40 }} />} color={overview.winRate >= 50 ? "#00FF94" : "#EF4444"} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard title="Net P&L" value={fmtUsd(overview.netPnl)} subtitle={`PF: ${overview.profitFactor === Infinity ? "∞" : fmt(overview.profitFactor)}`} icon={<AttachMoney sx={{ fontSize: 40 }} />} color={pnlColor(overview.netPnl)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard title="Daily P&L" value={fmtUsd(overview.dailyPnl)} subtitle={`${overview.dailyTrades} trades today`} icon={<CalendarMonth sx={{ fontSize: 40 }} />} color={pnlColor(overview.dailyPnl)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard title="Weekly P&L" value={fmtUsd(overview.weeklyPnl)} subtitle={`${overview.weeklyTrades} trades this week`} icon={<Schedule sx={{ fontSize: 40 }} />} color={pnlColor(overview.weeklyPnl)} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard title="Max Drawdown" value={fmtUsd(overview.maxDrawdown)} subtitle={`Fees: ${fmtUsd(overview.feesTotal)}`} icon={<TrendingDown sx={{ fontSize: 40 }} />} color="#F97316" />
          </Grid>
        </Grid>
      ) : null}

      {/* TABS */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<ShowChart />} label="Equity & P&L" iconPosition="start" />
          <Tab icon={<CalendarMonth />} label="P&L Calendar" iconPosition="start" />
          <Tab icon={<PieChartIcon />} label="Breakdowns" iconPosition="start" />
          <Tab icon={<TableChart />} label="Trades" iconPosition="start" />
          <Tab icon={<Insights />} label="Insights" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* TAB CONTENT */}
      {/* ====== TAB 0: EQUITY & P&L ====== */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Equity Curve */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Equity Curve (Net P&L)</Typography>
              <Box sx={{ height: 350 }}>
                {equityChartData ? (
                  <Line data={equityChartData} options={{ ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { ...chartDefaults.plugins.legend, position: "top" } } }} />
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <Typography color="text.secondary">{loading ? "Loading..." : "No equity data available"}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* P&L by Time Bucket */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>P&L by {granularity}</Typography>
              <Box sx={{ height: 300 }}>
                {pnlBarData ? (
                  <Bar data={pnlBarData} options={{ ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }} />
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <Typography color="text.secondary">{loading ? "Loading..." : "No data"}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Exit Reason Distribution */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Exit Reasons</Typography>
              <Box sx={{ height: 300, display: "flex", justifyContent: "center", alignItems: "center" }}>
                {exitReasonChart ? (
                  <Doughnut data={exitReasonChart} options={{ ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { position: "right", labels: { ...chartDefaults.plugins.legend.labels, boxWidth: 12, padding: 8 } } }, scales: {} }} />
                ) : (
                  <Typography color="text.secondary">{loading ? "Loading..." : "No data"}</Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Win Rate & Profit Factor Trend */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Win Rate & Profit Factor Trend</Typography>
              <Box sx={{ height: 280 }}>
                {winRateChartData ? (
                  <Line
                    data={winRateChartData}
                    options={{
                      ...chartDefaults,
                      scales: {
                        ...chartDefaults.scales,
                        y: { ...chartDefaults.scales.y, title: { display: true, text: "Win Rate %", color: "#999" }, min: 0, max: 100 },
                        y1: { position: "right", title: { display: true, text: "Profit Factor", color: "#999" }, min: 0, max: 5, ticks: { color: "#999" }, grid: { display: false } },
                      },
                    }}
                  />
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <Typography color="text.secondary">{loading ? "Loading..." : "No data"}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Fees Chart */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Fees Over Time</Typography>
              <Box sx={{ height: 280 }}>
                {feesChartData ? (
                  <Bar data={feesChartData} options={{ ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }} />
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <Typography color="text.secondary">{loading ? "Loading..." : "No data"}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ====== TAB 1: P&L CALENDAR ====== */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <PnlCalendar data={heatmapData} />
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ====== TAB 2: BREAKDOWNS ====== */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          {breakdowns?.bySymbol?.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Performance by Symbol</Typography>
                <BreakdownTable data={breakdowns.bySymbol} title="Symbol" />
              </Paper>
            </Grid>
          )}
          {breakdowns?.byBroker?.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Performance by Broker</Typography>
                <BreakdownTable data={breakdowns.byBroker} title="Broker" />
              </Paper>
            </Grid>
          )}
          {breakdowns?.byMode?.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Performance by Trade Mode</Typography>
                <BreakdownTable data={breakdowns.byMode} title="Mode" />
              </Paper>
            </Grid>
          )}
          {breakdowns?.byLeverage?.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Performance by Leverage</Typography>
                <BreakdownTable data={breakdowns.byLeverage} title="Leverage" />
              </Paper>
            </Grid>
          )}
          {breakdowns?.byExitReason?.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Performance by Exit Reason</Typography>
                <BreakdownTable data={breakdowns.byExitReason} title="Exit Reason" />
              </Paper>
            </Grid>
          )}
          {breakdowns?.byStrategy?.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Performance by Strategy</Typography>
                <BreakdownTable data={breakdowns.byStrategy} title="Strategy" />
              </Paper>
            </Grid>
          )}
          {(!breakdowns || Object.values(breakdowns).every(v => !v?.length)) && (
            <Grid item xs={12}>
              <Alert severity="info">No breakdown data available. Close some trades to see performance breakdowns.</Alert>
            </Grid>
          )}
        </Grid>
      )}

      {/* ====== TAB 3: TRADES ====== */}
      {activeTab === 3 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Trade History</Typography>
          <TradesTable filters={filters} />
        </Paper>
      )}

      {/* ====== TAB 4: INSIGHTS ====== */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                <Insights /> AI Insights & Recommendations
              </Typography>
              {insights?.insights?.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {insights.insights.map((insight, i) => (
                    <Alert
                      key={i}
                      severity={insight.toLowerCase().includes("warning") || insight.toLowerCase().includes("risk") ? "warning" : insight.toLowerCase().includes("good") || insight.toLowerCase().includes("strong") ? "success" : "info"}
                      sx={{ "& .MuiAlert-message": { fontSize: 13 } }}
                    >
                      {insight}
                    </Alert>
                  ))}
                </Box>
              ) : (
                <Alert severity="info">
                  {loading ? "Loading insights..." : "No insights available. Need more closed trades for pattern analysis."}
                </Alert>
              )}
            </Paper>
          </Grid>
          {/* Quick KPIs in insights tab */}
          {insights?.stats && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Performance Summary</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Expectancy / Trade</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: pnlColor(insights.stats.expectancy) }}>
                      {fmtUsd(insights.stats.expectancy)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Profit Factor</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: insights.stats.profitFactor >= 1 ? "#00FF94" : "#EF4444" }}>
                      {insights.stats.profitFactor === Infinity ? "∞" : fmt(insights.stats.profitFactor)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Avg Win / Avg Loss</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {fmtUsd(insights.stats.avgWin)} / {fmtUsd(insights.stats.avgLoss)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Avg Duration</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {insights.stats.avgDurationSeconds > 3600
                        ? `${(insights.stats.avgDurationSeconds / 3600).toFixed(1)}h`
                        : `${Math.round(insights.stats.avgDurationSeconds / 60)}m`}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* LOADING OVERLAY */}
      {loading && (
        <Box sx={{ position: "fixed", bottom: 24, right: 24, display: "flex", alignItems: "center", gap: 1, bgcolor: "background.paper", p: 1.5, borderRadius: 2, boxShadow: 3 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading analytics...</Typography>
        </Box>
      )}
    </Box>
  );
}
